"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateTimeMarshaller = exports.tryParseUnixMilliseconds = exports.unknownToInteger = void 0;
const luxon_1 = require("luxon");
const zod_1 = require("zod");
const ZodExtensions_1 = require("./ZodExtensions");
function validateMarshallerOptions(options) {
    //ensure either format or formats is set
    if (!options.parsingDelegate && !options.format && (!options.formats || options.formats.length < 1)) {
        return [false, {
                code: zod_1.z.ZodIssueCode.custom,
                message: 'DateTimeMarshaller requires either option "format", "formats", or "parsingDelegate" to be specified',
            }];
    }
    //formats is set
    if (options.formats) {
        //ensure the same format is not specified twice
        if (new Set(options.formats).size !== options.formats.length) {
            return [false, {
                    code: zod_1.z.ZodIssueCode.custom,
                    message: 'DateTimeMarshaller option "formats" must not contain the same format more than once',
                }];
        }
        //ensure both "unix" and "unix millis" are not specified
        const containsUnix = options.formats.some(format => format === 'unix');
        const containsUnixMillis = options.formats.some(format => format === 'unix millis');
        if (containsUnix && containsUnixMillis) {
            return [false, {
                    code: zod_1.z.ZodIssueCode.custom,
                    message: 'DateTimeMarshaller option "formats" must not contain both "unix" and "unix millis"',
                }];
        }
        //if "custom" is used, ensure one or more custom format is specified
        const containsCustom = options.formats.some(format => format === 'custom');
        if (containsCustom && (!options.customPatterns || options.customPatterns.length < 1)) {
            return [false, {
                    code: zod_1.z.ZodIssueCode.custom,
                    message: 'DateTimeMarshaller options must specify one or more "customPatterns" if option "formats" contains "custom"',
                }];
        }
    }
    else {
        //if "custom" is used, ensure one or more custom format is specified
        if (options.format === 'custom' && (!options.customPatterns || options.customPatterns.length < 1)) {
            return [false, {
                    code: zod_1.z.ZodIssueCode.custom,
                    message: 'DateTimeMarshaller options must specify one or more "customPatterns" if option "format" is "custom"',
                }];
        }
    }
    return [true, undefined];
}
function unknownToInteger(val) {
    let floatVal = undefined;
    switch (typeof val) {
        case 'number':
            floatVal = val;
            break;
        case 'string':
            floatVal = parseFloat(val);
            break;
    }
    return Number.isInteger(floatVal) ? floatVal : undefined;
}
exports.unknownToInteger = unknownToInteger;
function tryParseUnixMilliseconds(val) {
    if (typeof val === 'undefined') {
        return undefined;
    }
    return luxon_1.DateTime.fromMillis(val);
}
exports.tryParseUnixMilliseconds = tryParseUnixMilliseconds;
/* eslint-disable @typescript-eslint/no-explicit-any */
const DateTimeMarshaller = (v, options) => {
    /* eslint-enable @typescript-eslint/no-explicit-any */
    let resolved;
    return v
        .superRefine((val, ctx) => {
        let valueType = typeof val;
        const originalVal = val;
        const optionValidation = validateMarshallerOptions(options);
        if (optionValidation && !optionValidation[0]) {
            ctx.addIssue(optionValidation[1]);
            return;
        }
        //-1.) if valid Date object, just convert
        if (val instanceof Date && !isNaN(val.getTime())) {
            resolved = luxon_1.DateTime.fromJSDate(val);
            return;
        }
        // 0.) invoke the parsingDelegate, if set
        if (options.parsingDelegate) {
            try {
                val = options.parsingDelegate(val);
                if (val instanceof luxon_1.DateTime) {
                    if (!val.isValid) {
                        ctx.addIssue({
                            code: zod_1.z.ZodIssueCode.custom,
                            message: 'Parsing delegate returned an invalid DateTime object',
                        });
                    }
                    else {
                        resolved = val;
                    }
                    return;
                }
                else {
                    valueType = typeof val;
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }
            catch (e) {
                ctx.addIssue({
                    code: zod_1.z.ZodIssueCode.custom,
                    message: `DateTime parsing delegate returned an error: ${e.message}`,
                });
                return;
            }
        }
        // 1.) if nullable and value is null(ish), return undefined
        if (ZodExtensions_1.isNullish(val)) {
            if (options?.nullable === true) {
                resolved = val;
            }
            else {
                ctx.addIssue({
                    code: zod_1.z.ZodIssueCode.invalid_type,
                    message: 'value must not be nullish',
                    expected: 'unknown',
                    received: valueType,
                });
            }
            return;
        }
        else if (options?.nullable === true
            && val === ''
            && 'emptyStringAsNull' in options
            && options?.emptyStringAsNull === true) {
            resolved = null;
            return;
        }
        // 2.) attempt to parse the input
        const formats = options.format
            ? [options.format]
            : options.formats || /* istanbul ignore next */ [];
        for (const format of formats) {
            let parsedValue;
            switch (format) {
                case 'unix':
                    {
                        const timestamp = unknownToInteger(val);
                        /* istanbul ignore else: else case irrelevant */
                        if (typeof timestamp !== 'undefined') {
                            parsedValue = tryParseUnixMilliseconds(timestamp * 1000);
                        }
                        break;
                    }
                case 'unix millis':
                    {
                        const timestamp = unknownToInteger(val);
                        /* istanbul ignore else: else case irrelevant */
                        if (typeof timestamp !== 'undefined') {
                            parsedValue = tryParseUnixMilliseconds(timestamp);
                        }
                        break;
                    }
                case 'sql':
                    /* istanbul ignore else: else case irrelevant */
                    if (valueType === 'string') {
                        parsedValue = luxon_1.DateTime.fromSQL(val);
                    }
                    break;
                case 'rfc2822':
                    /* istanbul ignore else: else case irrelevant */
                    if (valueType === 'string') {
                        parsedValue = luxon_1.DateTime.fromRFC2822(val);
                    }
                    break;
                case 'iso8601':
                    /* istanbul ignore else: else case irrelevant */
                    if (valueType === 'string') {
                        parsedValue = luxon_1.DateTime.fromISO(val);
                    }
                    break;
                case 'http':
                    /* istanbul ignore else: else case irrelevant */
                    if (valueType === 'string') {
                        parsedValue = luxon_1.DateTime.fromHTTP(val);
                    }
                    break;
                case 'custom':
                    /* istanbul ignore else: else case irrelevant */
                    if (valueType === 'string') {
                        for (const pattern of options.customPatterns ||
                            /* istanbul ignore next */ []) {
                            parsedValue = luxon_1.DateTime.fromFormat(val, pattern);
                            /* istanbul ignore else: else case irrelevant */
                            if (parsedValue && parsedValue.isValid) {
                                break;
                            }
                        }
                    }
                    break;
            }
            if (parsedValue && parsedValue.isValid) {
                resolved = parsedValue;
                return;
            }
        }
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: `could not parse "${originalVal}" to a valid DateTime`,
        });
    })
        .transform(() => resolved ? resolved.setZone('utc') : resolved);
};
exports.DateTimeMarshaller = DateTimeMarshaller;
//# sourceMappingURL=ZodDateTimeMarshaller.js.map